import React from 'react';
import moment from 'moment';
import { CheckSquare, FileText, XOctagon, Receipt } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import Pagination from './Pagination/Pagination';
import { cuentaLabel } from '../services/cuentaBancaria.service';

function getStatusBadge(estado) {
  switch (estado) {
    case 'Confirmada':
      return <Badge variant='warning'>Confirmada</Badge>;
    case 'Verificada':
      return <Badge variant='success'>Verificada</Badge>;
    case 'Cancelada':
      return <Badge variant='destructive'>Cancelada</Badge>;
    case 'Automatizacion':
      return <Badge variant='secondary'>Automatizacion</Badge>;
    default:
      return <Badge variant='outline'>Pendiente</Badge>;
  }
}

function ReservaTable({
  title,
  reservas = [],
  totalCount = 0,
  currentPage = 1,
  onPageChange,
  itemsPerPage = 15,
  userInfo,
  onConfirmarPago,
  onDeleteReserva,
  onCancelarReserva,
  onVerificarPago,
  onDescargarBoleto,
  onVerComprobante,
  t,
  showBoardColumns = false,
  cuentas = [],
  onChangeCuenta,
}) {
  const isBoard = showBoardColumns;
  const isAdmin = Boolean(userInfo?.isAdmin);
  const showCuenta = cuentas.length > 0 || reservas.some(r => r.cuenta);

  return (
    <>
      <div className='px-4 py-3 text-sm font-medium text-foreground'>
        {title} ({totalCount})
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Numero</TableHead>
            <TableHead>Fecha</TableHead>
            <TableHead>Comprador</TableHead>
            <TableHead>Vendedor</TableHead>
            {isBoard && <TableHead>Casillas</TableHead>}
            {showCuenta && <TableHead>Destino</TableHead>}
            <TableHead>Estado</TableHead>
            {userInfo && userInfo.isAdmin && <TableHead />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {reservas.map((reserva, index) => (
            <TableRow key={`reserva-${reserva._id || index}`}>
              <TableCell>{reserva.tickets ? reserva.tickets.join('-') : ''}</TableCell>
              <TableCell>{moment(new Date(reserva.fechaReserva)).format('DD/MM/YY')}</TableCell>
              <TableCell>{reserva.comprador?.nombre || ''}</TableCell>
              <TableCell>{reserva.vendedor?.nombre || ''}</TableCell>
              {isBoard && (
                <TableCell>
                  {reserva.casillas && reserva.casillas.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {reserva.casillas.map((c, i) => (
                        <span
                          key={i}
                          style={{
                            backgroundColor: c.color || '#6c757d',
                            color: '#fff',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '0.8em',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {c.categoria} ({c.fila},{c.columna})
                        </span>
                      ))}
                    </div>
                  ) : (
                    '—'
                  )}
                </TableCell>
              )}
              {showCuenta && (
                <TableCell>
                  {isAdmin && onChangeCuenta && cuentas.length > 0 ? (
                    <select
                      aria-label='Destino de la compra'
                      value={reserva.cuenta?._id || reserva.cuenta || ''}
                      onChange={e => onChangeCuenta(reserva, e.target.value)}
                      title={reserva.cuenta?.beneficiario || ''}
                      className='h-8 max-w-[180px] rounded-md border border-input bg-background px-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring'
                    >
                      <option value=''>— Sin destino —</option>
                      {cuentas.map(c => (
                        <option key={c._id} value={c._id}>
                          {cuentaLabel(c)}
                          {c.activa === false ? ' (inactiva)' : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className='text-sm'>
                      {reserva.cuenta ? cuentaLabel(reserva.cuenta) : '—'}
                    </span>
                  )}
                </TableCell>
              )}
              <TableCell>
                <div className='flex items-center gap-2'>
                  {getStatusBadge(reserva.estado)}
                  {(reserva.estado === 'Confirmada' || reserva.estado === 'Verificada') && (
                    <Button
                      variant='ghost'
                      size='icon-sm'
                      title={t('reservation.viewReceipt')}
                      onClick={() => onVerComprobante(reserva)}
                    >
                      <Receipt className='h-4 w-4' />
                    </Button>
                  )}
                </div>
              </TableCell>
              {userInfo && userInfo.isAdmin && (
                <TableCell>
                  <div className='flex items-center gap-1'>
                    {reserva.estado === 'Pendiente' && (
                      <Button
                        variant='ghost'
                        size='icon-sm'
                        title={t('reservation.confirmPayment')}
                        onClick={() => onConfirmarPago(reserva)}
                      >
                        <CheckSquare className='h-4 w-4' />
                      </Button>
                    )}

                    {reserva.estado === 'Confirmada' && (
                      <Button
                        variant='ghost'
                        size='icon-sm'
                        title={t('reservation.verifyPayment')}
                        disabled={!userInfo.canVerify}
                        onClick={() => onVerificarPago(reserva)}
                      >
                        <CheckSquare className='h-4 w-4' />
                      </Button>
                    )}

                    {(reserva.estado === 'Confirmada' || reserva.estado === 'Verificada') && (
                      <Button
                        variant='ghost'
                        size='icon-sm'
                        title={t('reservation.downloadReceipt')}
                        onClick={() => onDescargarBoleto(reserva)}
                      >
                        <FileText className='h-4 w-4' />
                      </Button>
                    )}

                    <Button
                      variant='ghost'
                      size='icon-sm'
                      title={
                        reserva.estado === 'Cancelada'
                          ? t('reservation.deleteReservation')
                          : t('reservation.cancelReservation')
                      }
                      onClick={() =>
                        reserva.estado === 'Cancelada'
                          ? onDeleteReserva(reserva)
                          : onCancelarReserva(reserva)
                      }
                      disabled={
                        (reserva.estado === 'Verificada' && !userInfo.isSuperAdmin) ||
                        (reserva.estado === 'Confirmada' && !userInfo.canVerify)
                      }
                    >
                      <XOctagon className='h-4 w-4' />
                    </Button>
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Pagination
        className='pagination-bar'
        totalCount={totalCount}
        onPageChange={onPageChange}
        currentPage={currentPage}
        pageSize={itemsPerPage}
      />
    </>
  );
}

export default ReservaTable;
